/**
 * TechnicianDashboard — Feb-2026
 * ------------------------------
 * • Technicians see their own workload.
 * • Engineers / Managers can pick any technician from a dropdown to view
 *   the same widgets as a "Technician-wise" performance view.
 *
 * Widgets: KPI tiles, status pie, recent 8 complaints, materials return
 * status, completion rate, avg closed / day, avg closed / week.
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { ClipboardCheck, Package, Star, TrendingUp, Clock, RotateCcw, Users } from 'lucide-react';

const STATUS_COLORS = {
  open: '#f97316', new: '#f97316', assigned: '#0ea5e9', in_progress: '#a855f7',
  resolved: '#10b981', closed: '#22c55e', tenant_sign_pending: '#f59e0b',
};

export default function TechnicianDashboard() {
  const { user } = useContext(AuthContext);
  const canPickAny = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ENGINEER', 'CS_SUPERVISOR'].includes(user?.role);
  const [technicians, setTechnicians] = useState([]);
  const [pick, setPick] = useState(user?.id);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canPickAny) return;
    axios.get(`${API}/users`, { withCredentials: true })
      .then(r => setTechnicians((r.data || []).filter(u => u.role === 'TECHNICIAN')))
      .catch(() => {});
  }, [canPickAny]);

  useEffect(() => {
    setLoading(true);
    const params = pick && pick !== user?.id ? { technician_id: pick } : {};
    axios.get(`${API}/dashboard/technician`, { params, withCredentials: true })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [pick, user?.id]);

  const pieData = Object.entries(data?.status_buckets || {}).map(([k, v]) => ({ name: k.replace('_', ' '), value: v, fill: STATUS_COLORS[k] || '#94a3b8' }));

  return (
    <Layout>
      <div data-testid="technician-dashboard" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <ClipboardCheck className="w-6 h-6 text-teal-600" />
              {canPickAny && pick !== user?.id ? 'Technician-wise Performance' : 'My Work'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {canPickAny && pick !== user?.id ? 'Pick a technician to see their workload and KPI.' : 'Your assigned complaints, materials, and performance.'}
            </p>
          </div>
          {canPickAny && (
            <div className="min-w-[240px]">
              <Label className="text-xs">Technician</Label>
              <Select value={pick} onValueChange={setPick}>
                <SelectTrigger data-testid="pick-technician"><SelectValue placeholder="Pick technician…" /></SelectTrigger>
                <SelectContent>
                  {user?.role === 'TECHNICIAN' ? null : (
                    <SelectItem value={user?.id}>Me ({user?.name})</SelectItem>
                  )}
                  {technicians.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {loading ? (
          <div className="py-16 text-center text-muted-foreground">Loading dashboard…</div>
        ) : !data ? (
          <div className="py-16 text-center text-muted-foreground">No data</div>
        ) : (
          <>
            {/* KPI tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <KpiTile testId="kpi-assigned" icon={ClipboardCheck} label="Assigned" value={data.totals.assigned_complaints} color="teal" />
              <KpiTile testId="kpi-open" icon={Clock} label="Open" value={data.totals.open} color="orange" />
              <KpiTile testId="kpi-closed" icon={ClipboardCheck} label="Closed" value={data.totals.closed} color="emerald" />
              <KpiTile testId="kpi-completion" icon={TrendingUp} label="Completion Rate" value={`${data.completion_rate}%`} color="cyan" />
              <KpiTile testId="kpi-avg-day" icon={TrendingUp} label="Closed / day" value={data.avg_closed_per_day} color="sky" hint="30-day avg" />
              <KpiTile testId="kpi-rating" icon={Star} label="KPI Rating" value={`${data.kpi_rating.toFixed(1)} / 5`} color="amber" hint={`On-time ${data.on_time_rate}%`} />
            </div>

            {/* Charts + material return */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="p-4 lg:col-span-2">
                <div className="text-sm font-semibold text-muted-foreground mb-3">Complaints by status</div>
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={pieData} innerRadius={45} outerRadius={80} dataKey="value" label>
                        {pieData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <div className="py-14 text-center text-muted-foreground text-sm">No complaints yet</div>}
              </Card>
              <Card className="p-4">
                <div className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-1.5"><Package className="w-4 h-4" />Material return status</div>
                <div className="space-y-2 text-sm">
                  <Row label="Issued" value={data.materials.issued} color="text-teal-700" />
                  <Row label="Returned" value={data.materials.returned} color="text-emerald-700" />
                  <Row label="Pending returnable ↩︎" value={data.materials.returnable_pending} color="text-rose-700 font-semibold" testId="materials-returnable-pending" />
                </div>
                <div className="mt-4 pt-3 border-t text-xs text-slate-500">
                  <div>Closed last 30 days: <span className="font-semibold text-slate-800">{data.closed_last_30_days}</span></div>
                  <div>Closed / week (avg): <span className="font-semibold text-slate-800">{data.avg_closed_per_week}</span></div>
                </div>
              </Card>
            </div>

            {/* Weekly throughput bar chart */}
            <Card className="p-4">
              <div className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-1.5"><TrendingUp className="w-4 h-4" />Throughput snapshot</div>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={[
                  { k: 'Closed / day', v: data.avg_closed_per_day },
                  { k: 'Closed / week', v: data.avg_closed_per_week },
                  { k: 'On-time %', v: data.on_time_rate },
                  { k: 'Completion %', v: data.completion_rate },
                ]}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="k" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="v" fill="#0d9488" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* Recent complaints */}
            <Card className="p-4">
              <div className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-1.5"><Users className="w-4 h-4" />Recent complaints</div>
              {data.recent_complaints.length === 0 ? (
                <div className="py-6 text-center text-muted-foreground text-sm">No complaints assigned yet</div>
              ) : (
                <div className="space-y-2">
                  {data.recent_complaints.map(c => (
                    <a key={c.id} href={`/complaints?highlight=${c.id}`} className="flex items-center justify-between p-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800 border" data-testid={`recent-${c.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{c.title}</div>
                        <div className="text-[11px] text-slate-500">{c.ticket_number || c.id.slice(0,8)} · {c.category}</div>
                      </div>
                      <span className="ml-2 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                        {(c.status || 'open').replace('_', ' ')}
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}

function KpiTile({ icon: Icon, label, value, color, hint, testId }) {
  const map = {
    teal: 'from-teal-500/10 to-teal-600/10 text-teal-700 border-teal-300',
    orange: 'from-orange-500/10 to-orange-600/10 text-orange-700 border-orange-300',
    emerald: 'from-emerald-500/10 to-emerald-600/10 text-emerald-700 border-emerald-300',
    cyan: 'from-cyan-500/10 to-cyan-600/10 text-cyan-700 border-cyan-300',
    sky: 'from-sky-500/10 to-sky-600/10 text-sky-700 border-sky-300',
    amber: 'from-amber-500/10 to-amber-600/10 text-amber-700 border-amber-300',
  };
  return (
    <Card data-testid={testId} className={`p-3 bg-gradient-to-br ${map[color]} border`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <div className="text-[11px] uppercase tracking-wide font-semibold">{label}</div>
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      {hint && <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>}
    </Card>
  );
}
function Row({ label, value, color, testId }) {
  return (
    <div className="flex justify-between items-center" data-testid={testId}>
      <span className="text-slate-600 dark:text-slate-300">{label}</span>
      <span className={color}>{value}</span>
    </div>
  );
}
